import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { inputValueToUnixSeconds, unixSecondsToInputValue } from '@/lib';
import type { AttributeNode } from '@/types/schemas';

type FieldType = 'date' | 'datetime';
function isFieldType(v: unknown): v is FieldType {
  return v === 'date' || v === 'datetime';
}

export function LeafInput(props: {
  node: AttributeNode;
  label: string; // "Value" OR node.key
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const { node, label, value, onChange } = props;

  if (node.type === 'boolean') {
    return (
      <div className="flex items-center justify-between rounded-xl border p-3">
        <Label>{label}</Label>
        <Switch checked={Boolean(value)} onCheckedChange={(v) => onChange(v)} />
      </div>
    );
  }

  if (node.type === 'number') {
    const fieldType = node.xHv?.fieldType;
    const isDateLike = isFieldType(fieldType);

    if (isDateLike) {
      const inputType = fieldType === 'date' ? 'date' : 'datetime-local';
      return (
        <div className="space-y-2">
          <Label className="font-mono text-sm">
            {label} <span className="text-muted-foreground ml-2 text-xs">{fieldType}</span>
          </Label>

          <Input
            type={inputType}
            value={typeof value === 'number' ? unixSecondsToInputValue(value, fieldType) : ''}
            onChange={(e) => onChange(inputValueToUnixSeconds(e.target.value, fieldType))}
          />

          <p className="text-muted-foreground text-xs">Stored as unix seconds</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Label className="font-mono text-sm">{label}</Label>
        <Input
          type="number"
          value={typeof value === 'number' ? value : value == null ? '' : String(value)}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) ? n : undefined);
          }}
          placeholder="Enter a number"
        />
      </div>
    );
  }

  if (node.type === 'string') {
    if (node.enum?.length) {
      return (
        <div className="space-y-2">
          <Label>{label}</Label>
          <Select value={typeof value === 'string' ? value : ''} onValueChange={(v) => onChange(v)}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Select value" />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {node.enum.map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <Input
          value={typeof value === 'string' ? value : value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter a string"
        />
      </div>
    );
  }

  return (
    <div className="text-muted-foreground rounded-xl border p-3 text-sm">
      Unsupported type for <span className="font-mono">{label}</span>
    </div>
  );
}
